import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AccountPausedScreen } from '../features/auth/AccountPausedScreen';
import { AccessDeniedScreen } from './AccessDeniedScreen';
import { PERMISSION_LABELS, hasPermission, type Permission } from '../utils/permissions';
import { isProfileMissing, profileMissingTarget } from '../utils/profileMissing';

type UserRole = 'system_admin' | 'admin' | 'user';

interface ProtectedRouteProps {
  children?: ReactNode;
  /**
   * Roles that are allowed to access this route.
   * If not specified, any authenticated user can access.
   * If specified, user must have one of the listed roles.
   */
  allowedRoles?: UserRole[];
  /**
   * Page permission required to reach this route, withheld per staff member by an organization
   * admin. Checked after `allowedRoles`, and treated as satisfied when the profile carries no
   * permission list at all — see `hasPermission`.
   *
   * Client-side only. The API enforces the same restriction, which is what makes a bookmark or a
   * pasted URL fail rather than merely look tidy.
   */
  requiredPermission?: Permission;
  /**
   * Where to redirect if the user doesn't have the required role.
   * Defaults to '/players'.
   */
  unauthorizedRedirect?: string;
  /**
   * Render `AccessDeniedScreen` in place of a withheld page instead of redirecting to
   * `unauthorizedRedirect`.
   *
   * A behaviour flag, deliberately named as one — the page's own name comes from
   * `PERMISSION_LABELS`, so this carries no data. Opt-in rather than the default because every
   * existing gated route was written expecting the bounce, and flipping all nine is a change to
   * nine features rather than to this one. New gated routes should set it: a silent redirect to
   * /players is indistinguishable from a broken link.
   */
  explainDenial?: boolean;
}

export const ProtectedRoute = ({
  children,
  allowedRoles,
  requiredPermission,
  unauthorizedRedirect = '/players',
  explainDenial = false,
}: ProtectedRouteProps) => {
  const { user, initializing, organizationPaused, organizationAwaitingPayment } = useAuth();

  if (initializing) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontSize: '14px',
        color: 'var(--color-text-grey)'
      }}>
        Loading...
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // A Firebase account with no RosterPilot user - every request in here would be refused. An
  // unfinished self-serve signup (a stored signup for this address) goes to /signup, which resumes
  // it and never bounces such a user back. Anyone else (removed staff, most often) goes to /login,
  // which signs them out and says the email is not linked to an active account.
  if (isProfileMissing(user)) {
    return <Navigate to={profileMissingTarget(user)} replace />;
  }

  // Organization paused - block the app in place (no redirect, so the user stays
  // signed in and we avoid a redirect loop). System admins are never blocked.
  if (organizationPaused && user.profile?.role !== 'system_admin') {
    return <AccountPausedScreen />;
  }

  // Organization awaiting its first payment (self-serve signup) - send them to finish paying.
  // After the pause check, matching the API's own gate order. A redirect rather than an in-place
  // screen because /signup/payment is a real page: Stripe's cancel URL lands there too.
  if (organizationAwaitingPayment && user.profile?.role !== 'system_admin') {
    return <Navigate to="/signup/payment" replace />;
  }

  // Check role-based access
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = user.profile?.role as UserRole | undefined;

    if (!userRole || !allowedRoles.includes(userRole)) {
      return <Navigate to={unauthorizedRedirect} replace />;
    }
  }

  // Check page access withheld by the organization's admin. After the role check, so a role the
  // route does not allow is still reported as a role problem.
  if (requiredPermission && !hasPermission(user.profile, requiredPermission)) {
    return explainDenial ? (
      <AccessDeniedScreen pageName={PERMISSION_LABELS[requiredPermission]} />
    ) : (
      <Navigate to={unauthorizedRedirect} replace />
    );
  }

  // If children provided, render them; otherwise render Outlet for nested routes
  return children ? <>{children}</> : <Outlet />;
};
